import React, { useState } from 'react'
import { createPortal } from 'react-dom'
import { useIntl } from 'react-intl'
import { Button, Spin } from 'antd'

import IntlMessages from '../../../../util/IntlMessages'
import { useHideCrispWhile } from '../../../../hooks/use-hide-crisp-while'
import { FilterTrigger, useFilterRowLabels } from '../../../../design-system/FilterTrigger'
import { Text } from '../../../../design-system/Text'
import { IUserFilteredInfo } from '../../../../types/users'
import { MoveEventType, useBulkUserMove } from '../hooks/useBulkUserMove'
import type { IMoveTargetOptions } from '../columns/moveTargetOptions'
import type { ISelectionScope } from './selectAllScope'

type MovePhase = 'pick' | 'submitting'

interface IBulkMoveTriggerProps {
  label: string
  targets: IMoveTargetOptions
  eventType: MoveEventType
  selectedUsers: IUserFilteredInfo[]
  outOfScopeCount: (targetId: string) => number
  loadedOnlyNote?: string
}

const currentTargetId = (user: IUserFilteredInfo, eventType: MoveEventType): string | undefined => (eventType === 'TEAM_USER_MOVED' ? user.team?.id : user.location?.id)

const BulkMoveTrigger = (props: IBulkMoveTriggerProps): React.ReactElement => {
  const { label, targets, eventType, selectedUsers, outOfScopeCount, loadedOnlyNote } = props
  const bulkMove = useBulkUserMove()
  const rowLabels = useFilterRowLabels()

  const [open, setOpen] = useState(false)
  const [phase, setPhase] = useState<MovePhase>('pick')
  const [targetId, setTargetId] = useState<string | null>(null)

  const target = targets.options.find((option) => option.value === targetId)
  const skipped = targetId ? selectedUsers.filter((user) => currentTargetId(user, eventType) === targetId).length : 0
  const moving = targetId ? selectedUsers.length - skipped : 0
  const warningCount = targetId ? outOfScopeCount(targetId) : 0

  const reset = () => {
    setPhase('pick')
    setTargetId(null)
  }

  const submit = async () => {
    if (!targetId || !target) {
      return
    }
    setPhase('submitting')
    await bulkMove({
      eventType,
      targetId,
      targetName: target.label.trim(),
      users: selectedUsers.filter((user) => currentTargetId(user, eventType) !== targetId),
    })
    reset()
    setOpen(false)
  }

  const footer =
    phase === 'submitting' ? (
      <div className="vt-users-bulkmove-submitting">
        <IntlMessages id="users.table.bulkUpdating" values={{ count: moving }} />
        <Spin size="small" />
      </div>
    ) : (
      <div className="vt-users-bulkmove">
        {target && (
          <div className="vt-users-bulkmove-summary">
            <IntlMessages id="users.table.bulkMoveSummary" values={{ count: moving, target: target.label.trim(), skipped }} />
          </div>
        )}
        {loadedOnlyNote && (
          <div className="vt-users-bulkmove-summary">
            <Text tone="warning">{loadedOnlyNote}</Text>
          </div>
        )}
        {warningCount > 0 && (
          <div className="vt-users-bulkmove-warning">
            <IntlMessages id="users.table.bulkOutOfScopeWarning" values={{ count: warningCount }} />
          </div>
        )}
        {/* Only for a location move: brought-forward days are derived from the location, so changing
            it reworks the person's leave. Moving between departments does not. */}
        {target && eventType === 'LOCATION_USER_MOVED' && (
          <div className="vt-users-bulkmove-warning">
            <IntlMessages id="components.locationForm.warningInitialRolloverUsers" />
          </div>
        )}
        <div className="vt-users-bulkmove-actions">
          <Button type="primary" size="small" disabled={!targetId || moving === 0} onClick={submit}>
            <IntlMessages id="app.confirm" />
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              reset()
              setOpen(false)
            }}
          >
            <IntlMessages id="app.cancel" />
          </Button>
        </div>
      </div>
    )

  return (
    <FilterTrigger
      config={{
        id: eventType,
        label,
        options: targets.options,
        keepOrder: targets.keepOrder,
        multiple: false,
      }}
      searchLabel={rowLabels.search}
      noResultsLabel={rowLabels.noResults}
      value={targetId ? [targetId] : []}
      onChange={(next) => setTargetId(next[0] ?? null)}
      clearable={false}
      quiet
      placement="topLeft"
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (!nextOpen && phase !== 'submitting') {
          reset()
        }
      }}
      footer={footer}
    />
  )
}

export interface IBulkActionsBarProps {
  selectedUsers: IUserFilteredInfo[]
  status: string
  teams: IMoveTargetOptions
  locations: IMoveTargetOptions
  /** The caller decides: on a location's own tab one remaining target is a real destination. */
  canChangeLocation: boolean
  isLocationAdmin: boolean
  managedLocationIds: string[]
  /** i18n id for the location trigger, so a scoped table can say "Move to another location". */
  locationLabelId?: string
  onClear: () => void
  onBulkStatusChange: (status: string) => void
  /** How far the selection reaches. Set, the count reads against the filtered total rather than
   * standing on its own. */
  scope?: ISelectionScope
  /** Said in every confirmation a Select all cannot reach everyone from. */
  loadedOnlyNote?: string
}

export const BulkActionsBar = (props: IBulkActionsBarProps): React.ReactElement => {
  const {
    selectedUsers,
    status,
    teams,
    locations,
    canChangeLocation,
    isLocationAdmin,
    managedLocationIds,
    locationLabelId = 'users.table.bulkChangeLocation',
    onClear,
    onBulkStatusChange,
    scope,
    loadedOnlyNote,
  } = props
  const { formatMessage } = useIntl()
  // On a narrow screen the bar reaches the bottom-right corner, where the Crisp launcher would cover it
  useHideCrispWhile(true)

  const locationOutOfScopeCount = (targetId: string): number => {
    if (!isLocationAdmin || managedLocationIds.includes(targetId)) {
      return 0
    }
    return selectedUsers.filter((user) => user.location?.id !== targetId).length
  }

  // Portalled to the body: the bar is position:fixed and means the viewport, but a host that mounts
  // the table inside antd Tabs gives it an ancestor whose transform makes "fixed" mean that pane
  // instead, so the bar lands in the wrong place. Dark mode is keyed off body, so it still applies.
  return createPortal(
    <div className="vt-users-bulkbar">
      <span className="vt-users-bulkbar-count">
        <Text size="lg">
          {scope ? (
            <IntlMessages id="users.bulk.selectedOfTotal" values={{ count: selectedUsers.length, total: scope.total }} />
          ) : (
            <IntlMessages id="users.table.bulkSelectedCount" values={{ count: selectedUsers.length }} />
          )}
        </Text>
      </span>
      <Button size="small" type="text" onClick={onClear}>
        <IntlMessages id="users.table.clearSelection" />
      </Button>
      {teams.options.length > 1 && (
        <BulkMoveTrigger
          label={formatMessage({ id: 'users.table.bulkChangeDepartment' })}
          targets={teams}
          eventType="TEAM_USER_MOVED"
          selectedUsers={selectedUsers}
          loadedOnlyNote={loadedOnlyNote}
          outOfScopeCount={() => 0}
        />
      )}
      {canChangeLocation && locations.options.length > 0 && (
        <BulkMoveTrigger
          label={formatMessage({ id: locationLabelId })}
          targets={locations}
          eventType="LOCATION_USER_MOVED"
          selectedUsers={selectedUsers}
          loadedOnlyNote={loadedOnlyNote}
          outOfScopeCount={locationOutOfScopeCount}
        />
      )}
      {status === 'ACTIVE' && (
        <Button danger onClick={() => onBulkStatusChange('INACTIVE')}>
          <IntlMessages id="users.deactivate" />
        </Button>
      )}
      {status === 'INACTIVE' && (
        <Button type="primary" onClick={() => onBulkStatusChange('ACTIVE')}>
          <IntlMessages id="app.activate" />
        </Button>
      )}
    </div>,
    document.body
  )
}
